// client/src/components/developers/ApiDocsPage.jsx
//
// /developers — public API documentation (Roadmap Phase 6.1), generated from
// GET /api/v1/openapi.json (server/api/openapi.js) so it can't drift from the
// API: authentication, every endpoint with its permission, parameters and
// fields, and the webhook events with an example and how to check signatures.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOpenApi } from '../../services/developersApi';
import '../donations/Donations.css';

const METHOD_COLOR = { get: '#1d4ed8', post: '#047857', delete: '#b91c1c', put: '#92400e' };

function resolve(spec, schema) {
  if (schema && schema.$ref) return spec.components.schemas[schema.$ref.split('/').pop()];
  return schema;
}

function Fields({ spec, schema }) {
  const s = resolve(spec, schema);
  if (!s || !s.properties) return null;
  const required = new Set(s.required || []);
  return (
    <table className="dn-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <tbody>
        {Object.entries(s.properties).map(([k, v]) => {
          const p = resolve(spec, v) || {};
          const type = Array.isArray(p.type) ? p.type.filter((x) => x !== 'null').join(' | ') : (p.type || (v.$ref ? v.$ref.split('/').pop() : 'object'));
          return (
            <tr key={k} style={{ borderTop: '1px solid #e5e7eb' }}>
              <td style={{ padding: '6px 8px 6px 0', verticalAlign: 'top', whiteSpace: 'nowrap' }}><code>{k}</code>{required.has(k) && <span style={{ color: '#b91c1c' }}> *</span>}</td>
              <td style={{ padding: '6px 8px', verticalAlign: 'top', color: '#4b5563' }}>{type}{p.enum ? `: ${p.enum.join(', ')}` : ''}</td>
              <td style={{ padding: '6px 0', verticalAlign: 'top' }}>{p.description || ''}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const VERIFY_SNIPPET = `// Node.js: check a webhook really came from On-Screen
const crypto = require('crypto');

function verify(rawBody, header, secret) {
  const m = /^t=(\\d+),v1=([a-f0-9]{64})$/.exec(header || '');
  if (!m || Math.abs(Date.now() / 1000 - Number(m[1])) > 300) return false;
  const expected = crypto.createHmac('sha256', secret).update(\`\${m[1]}.\${rawBody}\`).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected, 'hex'), Buffer.from(m[2], 'hex'));
}

// verify(req.rawBody, req.headers['onscreen-signature'], process.env.ONSCREEN_WEBHOOK_SECRET)`;

export default function ApiDocsPage() {
  const { t } = useTranslation();
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { getOpenApi().then(setSpec).catch(() => setError(true)); }, []);

  if (error) return <main className="dn-public"><div className="dn-shell"><div className="dn-card"><p className="dn-error" role="alert">{t('apiDocs.loadError', 'The API description could not be loaded.')}</p></div></div></main>;
  if (!spec) return <main className="dn-public"><div className="dn-shell"><div className="dn-card">{t('common.loading', 'Loading…')}</div></div></main>;

  const server = spec.servers && spec.servers[0] ? spec.servers[0].url : '/api/v1';
  return (
    <main className="dn-public">
      <div className="dn-shell" style={{ maxWidth: 900 }}>
        <section className="dn-card">
          <h1 className="dn-title">{spec.info.title}</h1>
          {String(spec.info.description).split('\n\n').map((p, i) => <p key={i} className="dn-desc" style={{ marginBottom: 10 }}>{p.replace(/`/g, '')}</p>)}
          <p className="dn-note">{t('apiDocs.base', 'Base URL')}: <code>{server}</code> · <a href={`${server}/openapi.json`}>openapi.json</a></p>
        </section>

        <section className="dn-card">
          <h2 className="dn-title" style={{ fontSize: 20 }}>{t('apiDocs.auth', 'Authentication')}</h2>
          <pre style={{ background: '#f4f6fb', color: '#1f2937', padding: 12, borderRadius: 8, overflowX: 'auto' }}>{`curl ${server}/me \\\n  -H "Authorization: Bearer os_…"`}</pre>
        </section>

        <section className="dn-card" data-testid="endpoints">
          <h2 className="dn-title" style={{ fontSize: 20 }}>{t('apiDocs.endpoints', 'Endpoints')}</h2>
          {Object.entries(spec.paths).map(([path, ops]) => Object.entries(ops).map(([method, op]) => (
            <details key={`${method} ${path}`} style={{ borderTop: '1px solid #e5e7eb', padding: '10px 0' }} data-testid="endpoint">
              <summary style={{ cursor: 'pointer' }}>
                <strong style={{ color: METHOD_COLOR[method] || '#1f2937', textTransform: 'uppercase', marginRight: 8 }}>{method}</strong>
                <code>{path}</code> · {op.summary}
              </summary>
              <div style={{ paddingTop: 8 }}>
                {op['x-required-scope'] && <p className="dn-note">{t('apiDocs.permission', 'Permission')}: <code>{op['x-required-scope']}</code></p>}
                {(op.parameters || []).length > 0 && (
                  <p className="dn-note">{t('apiDocs.params', 'Parameters')}: {op.parameters.map((p) => <code key={p.name} style={{ marginRight: 8 }}>{p.name}</code>)}</p>
                )}
                {op.requestBody && (
                  <>
                    <p className="dn-goal-title">{t('apiDocs.body', 'Body (JSON)')}</p>
                    <Fields spec={spec} schema={op.requestBody.content['application/json'].schema} />
                  </>
                )}
                {op.responses && op.responses['200'] && op.responses['200'].content && (
                  <>
                    <p className="dn-goal-title" style={{ marginTop: 8 }}>{t('apiDocs.returns', 'Returns')}</p>
                    <Fields spec={spec} schema={op.responses['200'].content['application/json'].schema} />
                  </>
                )}
              </div>
            </details>
          )))}
        </section>

        <section className="dn-card" data-testid="webhook-docs">
          <h2 className="dn-title" style={{ fontSize: 20 }}>{t('apiDocs.webhooks', 'Webhooks')}</h2>
          <p className="dn-desc">{spec.webhooks && Object.values(spec.webhooks)[0] ? Object.values(spec.webhooks)[0].post.description : ''}</p>
          {Object.entries(spec.webhooks || {}).map(([type, w]) => (
            <details key={type} style={{ borderTop: '1px solid #e5e7eb', padding: '10px 0' }}>
              <summary style={{ cursor: 'pointer' }}><code>{type}</code> · {w.post.summary}</summary>
              <pre style={{ background: '#f4f6fb', color: '#1f2937', padding: 12, borderRadius: 8, overflowX: 'auto', fontSize: 13 }}>
                {JSON.stringify(w.post.requestBody.content['application/json'].example, null, 2)}
              </pre>
            </details>
          ))}
          <p className="dn-goal-title" style={{ marginTop: 12 }}>{t('apiDocs.verify', 'Checking the signature')}</p>
          <pre style={{ background: '#f4f6fb', color: '#1f2937', padding: 12, borderRadius: 8, overflowX: 'auto', fontSize: 13 }}>{VERIFY_SNIPPET}</pre>
        </section>
        <p className="dn-foot">On-Screen</p>
      </div>
    </main>
  );
}
